import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, Plus, RotateCcw, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronDown, Users, CheckSquare, Square, GitBranch, LayoutGrid, Crown, Shuffle, UserPlus, UserMinus } from 'lucide-react';
import EmployeePhoto from './EmployeePhoto';
import { api } from '../services/api';
import type { NewRoundModalProps, Employee, PagedResult } from '../types';
import { DEFAULT_QUESTIONS } from '../constants/defaultQuestions';
import { getEstimatedComparisons, getBlockInfo } from '../utils/pairGeneration';

const EMPLOYEE_PAGE_SIZE = 20;

const NewRoundModal: React.FC<NewRoundModalProps> = ({ isOpen, onClose, onCreate, loading, currentUserId }) => {
  // Step state: 1 = round config, 2 = employee selection, 3 = evaluator selection, 4 = group mapping (grouped only)
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  // Step 1 state
  const [newRoundName, setNewRoundName] = useState('');
  const [skipQuestions, setSkipQuestions] = useState(false);
  const [roundQuestions, setRoundQuestions] = useState([...DEFAULT_QUESTIONS]);
  const [comparisonMode, setComparisonMode] = useState<'full' | 'partial'>('full');
  const [accuracy, setAccuracy] = useState(70); // partial mode only: 30-100%
  const [isGrouped, setIsGrouped] = useState(false);

  // Step 2 state (employee selection)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedManager, setSelectedManager] = useState<string>('');
  const [managers, setManagers] = useState<string[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagedResult, setPagedResult] = useState<PagedResult<Employee> | null>(null);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployeeIDs, setSelectedEmployeeIDs] = useState<Set<number>>(new Set());
  const [totalEmployeeCount, setTotalEmployeeCount] = useState(0);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  // Cache for displaying selected employees in right panel
  const [employeeCache, setEmployeeCache] = useState<Map<number, { name: string; photoURL: string; title: string; department: string; manager: string }>>(new Map());
  const [selectedSearchTerm, setSelectedSearchTerm] = useState('');

  // Step 3 state (evaluator selection)
  const [evalSearchTerm, setEvalSearchTerm] = useState('');
  const [evalDebouncedSearch, setEvalDebouncedSearch] = useState('');
  const [evalPage, setEvalPage] = useState(1);
  const [evalPagedResult, setEvalPagedResult] = useState<PagedResult<Employee> | null>(null);
  const [evalEmployeesLoading, setEvalEmployeesLoading] = useState(false);
  const [selectedEvaluators, setSelectedEvaluators] = useState<Map<number, string>>(new Map());
  const [currentUserEmployee, setCurrentUserEmployee] = useState<Employee | null>(null);
  // Cache for displaying selected evaluators in right panel
  const [evaluatorCache, setEvaluatorCache] = useState<Map<number, { name: string; photoURL: string; title: string; department: string }>>(new Map());
  const [evalSelectedSearchTerm, setEvalSelectedSearchTerm] = useState('');
  const [evalSelectedManager, setEvalSelectedManager] = useState<string>('');
  // Grouped mode extras
  const [finalReviewerId, setFinalReviewerId] = useState<number | null>(null);
  const [frSelectionCount, setFrSelectionCount] = useState<number | null>(null);
  // Secondary evaluator state
  const [enableSecondary, setEnableSecondary] = useState(false);
  // Per-primary: which secondary evaluator IDs are selected — Map<primaryId, Set<secondaryId>>
  const [perPrimarySecondaries, setPerPrimarySecondaries] = useState<Map<number, Set<number>>>(new Map());
  // Per-employee secondary overrides: Map<primaryId, Map<employeeId, secondaryId>>
  // When not set for an employee, defaults to the first secondary of that primary
  // Employees data for secondary step
  const [secondaryStepEmployees, setSecondaryStepEmployees] = useState<Employee[]>([]);
  const [secondaryStepLoading, setSecondaryStepLoading] = useState(false);
  // All employees for secondary evaluator dropdown (unfiltered)
  const [step5AllEmployees, setStep5AllEmployees] = useState<Employee[]>([]);
  // Secondary step dropdown management
  const [step5OpenDropdown, setStep5OpenDropdown] = useState<number | null>(null);
  const [step5SearchTerm, setStep5SearchTerm] = useState('');
  // Expanded employee sections per primary (Set of primaryIds)
  const [step5ExpandedCards, setStep5ExpandedCards] = useState<Set<number>>(new Set());
  // Step 4 state (group mapping — grouped mode only)
  // Map<evaluatorId, Set<employeeId>>
  const [evaluatorGroupMappings, setEvaluatorGroupMappings] = useState<Map<number, Set<number>>>(new Map());
  const [step4Employees, setStep4Employees] = useState<Employee[]>([]);
  const [step4Loading, setStep4Loading] = useState(false);

  // Cached all-employees result shared across steps 3/4/5 (avoids redundant fetches)
  const allEmployeesCache = useRef<Employee[] | null>(null);
  const fetchAllEmployeesOnce = useCallback(async (): Promise<Employee[]> => {
    if (allEmployeesCache.current) return allEmployeesCache.current;
    const all = await api.getEmployees();
    allEmployeesCache.current = all;
    return all;
  }, []);

  // Track which participant set was used when step 3 last initialized,
  // so we can detect if the user went back to step 2 and changed selections.
  const step3ParticipantSnapshot = useRef<Set<number> | null>(null);
  // Track participant IDs that are also managers — these should appear in the evaluator candidate list
  const [participantManagerIds, setParticipantManagerIds] = useState<Set<number>>(new Set());

  // Debounce search (step 2)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce search (step 3)
  useEffect(() => {
    const timer = setTimeout(() => setEvalDebouncedSearch(evalSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [evalSearchTerm]);

  // Reset to page 1 on search or manager filter change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedManager]);

  // Reset evaluator page on search or manager filter change
  useEffect(() => {
    setEvalPage(1);
  }, [evalDebouncedSearch, evalSelectedManager]);

  // Fetch employees when on step 2
  useEffect(() => {
    if (!isOpen || step !== 2) return;
    let cancelled = false;
    const fetchData = async () => {
      setEmployeesLoading(true);
      try {
        const result = await api.getEmployeesPaged(
          page,
          EMPLOYEE_PAGE_SIZE,
          debouncedSearch || undefined,
          undefined,  // sortField
          undefined,  // sortDirection
          selectedManager || undefined  // manager filter
        );
        if (!cancelled) {
          setPagedResult(result);
          // Merge into cache for right-panel display
          setEmployeeCache(prev => {
            const next = new Map(prev);
            result.items.forEach(e => next.set(e.employeeID, { name: e.name, photoURL: e.photoURL, title: e.title, department: e.department, manager: e.manager }));
            return next;
          });
        }
      } catch (err) {
        console.error('Error fetching employees:', err);
      } finally {
        if (!cancelled) setEmployeesLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [isOpen, step, page, debouncedSearch, selectedManager]);

  // Track total employee count from the first unfiltered paged result
  const unfilteredTotal = useRef<number>(0);
  useEffect(() => {
    if (pagedResult && !debouncedSearch && !selectedManager) {
      unfilteredTotal.current = pagedResult.totalCount;
      setTotalEmployeeCount(pagedResult.totalCount);
    }
  }, [pagedResult, debouncedSearch, selectedManager]);

  // Load managers when entering step 2 or 3
  useEffect(() => {
    if (!isOpen || (step !== 2 && step !== 3)) return;
    if (managers.length > 0) return; // already loaded
    const loadManagers = async () => {
      setManagersLoading(true);
      try {
        const managerList = await api.getDistinctManagers();
        setManagers(managerList);
      } catch (err) {
        console.error('Error loading managers:', err);
        setManagers([]);
      } finally {
        setManagersLoading(false);
      }
    };
    loadManagers();
  }, [isOpen, step, managers.length]);


  // Auto-populate managers of selected employees as evaluators using targeted API.
  // Also fetches current user for pinning at top of evaluator list.
  useEffect(() => {
    if (!isOpen || step !== 3) return;
    let cancelled = false;

    // Check if participants changed since last step 3 visit
    const prevSnapshot = step3ParticipantSnapshot.current;
    const participantsChanged = prevSnapshot !== null && (
      prevSnapshot.size !== selectedEmployeeIDs.size ||
      [...selectedEmployeeIDs].some(id => !prevSnapshot.has(id))
    );

    const initStep3 = async () => {
      try {
        if (participantsChanged) {
          // Participants changed — reset evaluator selections and dependent state
          setSelectedEvaluators(new Map());
          setEvaluatorGroupMappings(new Map());
          setPerPrimarySecondaries(new Map());
        }

        if (!prevSnapshot || participantsChanged) {
          const participantIds = Array.from(selectedEmployeeIDs);

          const [managerEmployees, currentUser] = await Promise.all([
            api.getManagersOf(participantIds),
            (!prevSnapshot && currentUserId) ? api.getCurrentUser() : Promise.resolve(null),
          ]);

          if (cancelled) return;

          if (currentUser) setCurrentUserEmployee(currentUser);

          // Track which participants are also managers — they should appear in the evaluator candidate list
          const pManagerIds = new Set<number>();
          managerEmployees.forEach(m => {
            if (selectedEmployeeIDs.has(m.employeeID)) pManagerIds.add(m.employeeID);
          });
          setParticipantManagerIds(pManagerIds);

          // Auto-select all managers as evaluators (including participant-managers like E3 who manages E1, E2)
          const newEvaluatorIds = new Set<number>();
          if (managerEmployees.length > 0) {
            setSelectedEvaluators(prev => {
              const next = new Map(prev);
              managerEmployees.forEach(m => {
                next.set(m.employeeID, m.name);
                newEvaluatorIds.add(m.employeeID);
              });
              return next;
            });
            setEvaluatorCache(prev => {
              const next = new Map(prev);
              managerEmployees.forEach(m => next.set(m.employeeID, { name: m.name, photoURL: m.photoURL, title: m.title, department: m.department }));
              return next;
            });
          }

          // Preserve FR only if still in the new evaluator set, otherwise reset
          if (participantsChanged) {
            setFinalReviewerId(prev => {
              if (prev !== null && newEvaluatorIds.has(prev)) return prev;
              return null;
            });
            setFrSelectionCount(prev => {
              if (finalReviewerId !== null && newEvaluatorIds.has(finalReviewerId)) return prev;
              return null;
            });
          }

          step3ParticipantSnapshot.current = new Set(selectedEmployeeIDs);
        }
        // else: same participants, same data — nothing to do
      } catch (err) {
        console.error('Error initializing step 3:', err);
      }
    };

    initStep3();
    return () => { cancelled = true; };
  }, [isOpen, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch evaluator candidates via server-side pagination
  // Exclude participants except those who are also managers (participant-managers)
  useEffect(() => {
    if (!isOpen || step !== 3) return;
    let cancelled = false;
    const fetchEvaluators = async () => {
      setEvalEmployeesLoading(true);
      try {
        const excludeIds = Array.from(selectedEmployeeIDs).filter(id => !participantManagerIds.has(id));
        const result = await api.getEmployeesPaged(
          evalPage,
          EMPLOYEE_PAGE_SIZE,
          evalDebouncedSearch || undefined,
          undefined, // sortField
          undefined, // sortDirection
          evalSelectedManager || undefined,
          excludeIds.length > 0 ? excludeIds : undefined
        );
        if (!cancelled) {
          setEvalPagedResult(result);
          // Merge into evaluator cache for right-panel display
          setEvaluatorCache(prev => {
            const next = new Map(prev);
            result.items.forEach(e => next.set(e.employeeID, { name: e.name, photoURL: e.photoURL, title: e.title, department: e.department }));
            return next;
          });
        }
      } catch (err) {
        console.error('Error fetching evaluator candidates:', err);
      } finally {
        if (!cancelled) setEvalEmployeesLoading(false);
      }
    };
    fetchEvaluators();
    return () => { cancelled = true; };
  }, [isOpen, step, evalPage, evalDebouncedSearch, evalSelectedManager, selectedEmployeeIDs, participantManagerIds]);

  // Fetch all selected employee records when entering Step 4 (needed to show names in mapping UI)
  useEffect(() => {
    if (!isOpen || step !== 4) return;
    let cancelled = false;
    const fetchEmployees = async () => {
      setStep4Loading(true);
      try {
        const all = await fetchAllEmployeesOnce();
        if (!cancelled) setStep4Employees(all.filter(e => selectedEmployeeIDs.has(e.employeeID)));
      } catch (err) {
        console.error('Error fetching employees for mapping:', err);
      } finally {
        if (!cancelled) setStep4Loading(false);
      }
    };
    fetchEmployees();
    return () => { cancelled = true; };
  }, [isOpen, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch employees for secondary step (need to show employee lists per primary + all employees for dropdown)
  const secStepNum = isGrouped ? 5 : 4;
  useEffect(() => {
    if (!isOpen || step !== secStepNum || !enableSecondary) return;

    // Reuse already-loaded employees from previous steps if available
    if (step4Employees.length > 0 && step5AllEmployees.length > 0) {
      setSecondaryStepEmployees(step4Employees);
      return;
    }

    let cancelled = false;
    const fetchEmps = async () => {
      setSecondaryStepLoading(true);
      try {
        const all = await fetchAllEmployeesOnce();
        if (!cancelled) {
          setStep5AllEmployees(all);
          if (step4Employees.length > 0) {
            setSecondaryStepEmployees(step4Employees);
          } else {
            setSecondaryStepEmployees(all.filter(e => selectedEmployeeIDs.has(e.employeeID)));
          }
        }
      } catch (err) {
        console.error('Error fetching employees for secondary step:', err);
      } finally {
        if (!cancelled) setSecondaryStepLoading(false);
      }
    };
    fetchEmps();
    return () => { cancelled = true; };
  }, [isOpen, step, secStepNum, enableSecondary]); // eslint-disable-line react-hooks/exhaustive-deps

  const addQuestion = () => {
    setRoundQuestions([...roundQuestions, { text: '' }]);
  };

  const removeQuestion = (index: number) => {
    if (roundQuestions.length > 1) {
      setRoundQuestions(roundQuestions.filter((_, i) => i !== index));
    }
  };

  const updateQuestion = (index: number, text: string) => {
    const updated = [...roundQuestions];
    updated[index] = { text };
    setRoundQuestions(updated);
  };

  const handleResetToDefaults = () => {
    setRoundQuestions([...DEFAULT_QUESTIONS]);
    setSkipQuestions(false);
  };

  // Step 2: employee toggle — functional update fixes stale closure on rapid clicks
  const toggleEmployee = useCallback((employeeId: number) => {

    setSelectedEmployeeIDs(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = async () => {
    setSelectAllLoading(true);
    try {
      const ids = await api.getEmployeeIds(
        debouncedSearch || undefined,
        selectedManager || undefined
      );
      setSelectedEmployeeIDs(prev => {
        const next = new Set(prev);
        ids.forEach((id: number) => next.add(id));
        return next;
      });
    } catch (err) {
      console.error('Error selecting all employees:', err);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const handleDeselectAll = useCallback(() => {
    setSelectedEmployeeIDs(new Set());
  }, []);

  // Step 3: evaluator toggle — functional update fixes stale closure on rapid clicks
  // Also clears FR designation if the deselected evaluator was the Final Reviewer
  const toggleEvaluator = useCallback((emp: { employeeID: number; name: string; photoURL: string; title: string; department: string }) => {

    setSelectedEvaluators(prev => {
      const next = new Map(prev);
      if (next.has(emp.employeeID)) {
        next.delete(emp.employeeID);
      } else {
        next.set(emp.employeeID, emp.name);
      }
      return next;
    });
    // Ensure the evaluator is in the cache for right-panel display
    setEvaluatorCache(prev => {
      if (prev.has(emp.employeeID)) return prev;
      const next = new Map(prev);
      next.set(emp.employeeID, { name: emp.name, photoURL: emp.photoURL, title: emp.title, department: emp.department });
      return next;
    });
    setFinalReviewerId(prev => prev === emp.employeeID ? null : prev);
  }, []);

  const handleSelectAllEvaluators = async () => {
    try {
      const ids = await api.getEmployeeIds(
        evalDebouncedSearch || undefined,
        evalSelectedManager || undefined
      );
      // Build name lookup from the currently loaded page
      const nameMap = new Map<number, string>();
      (evalPagedResult?.items ?? []).forEach(emp => nameMap.set(emp.employeeID, emp.name));

      setSelectedEvaluators(prev => {
        const next = new Map(prev);
        ids.forEach(id => {
          // Exclude employees selected as participants — they cannot be primary evaluators
          if (!next.has(id) && !selectedEmployeeIDs.has(id)) {
            next.set(id, nameMap.get(id) ?? '');
          }
        });
        return next;
      });
    } catch (err) {
      console.error('Error selecting all evaluators:', err);
    }
  };

  const handleDeselectAllEvaluators = useCallback(() => {
    setSelectedEvaluators(new Map());
  }, []);

  const handleCreate = async (overrideGroupMappings?: Map<number, Set<number>> | React.MouseEvent) => {
    const mappings = overrideGroupMappings instanceof Map ? overrideGroupMappings : evaluatorGroupMappings;
    const groupMappingsArray = isGrouped
      ? Array.from(mappings.entries()).map(([evaluatorId, empSet]) => ({
        evaluatorId,
        employeeIds: Array.from(empSet),
      }))
      : undefined;

    // Build secondary mappings: each secondary evaluator independently evaluates the same employees as the primary
    const hasAnySecondary = enableSecondary && Array.from(perPrimarySecondaries.values()).some(s => s.size > 0);
    const secondaryMappingsArray = hasAnySecondary
      ? Array.from(selectedEvaluators.keys())
        .filter(primaryId => primaryId !== finalReviewerId)
        .flatMap(primaryId => {
          const secIds = perPrimarySecondaries.get(primaryId);
          if (!secIds || secIds.size === 0) return [];
          const primaryEmps = getEmployeesForPrimary(primaryId);
          // Each secondary evaluator gets the full set of employees from the primary
          return Array.from(secIds).map(secId => ({
            primaryEvaluatorId: primaryId,
            secondaryEvaluatorId: secId,
            employeeIds: primaryEmps,
          }));
        })
      : undefined;

    const data = {
      roundName: newRoundName,
      questions: skipQuestions ? [] : roundQuestions,
      selectedEmployeeIDs: Array.from(selectedEmployeeIDs),
      selectedEvaluators: Array.from(selectedEvaluators.entries()).map(([id, name]) => ({ id, name })),
      comparisonMode,
      accuracy: comparisonMode === 'partial' ? accuracy : undefined,
      isGrouped: isGrouped || undefined,
      finalReviewerId: isGrouped && finalReviewerId ? finalReviewerId : undefined,
      evaluatorGroupMappings: groupMappingsArray,
      hasSecondaryEvaluators: hasAnySecondary ? true : undefined,
      secondaryEvaluatorMappings: secondaryMappingsArray,
      frSelectionCount: isGrouped && finalReviewerId && frSelectionCount ? frSelectionCount : undefined,
    };

    try {
      await onCreate(data);
      resetState();
    } catch {
      // Creation failed — stay on current step so user can retry
    }
  };

  const resetState = () => {
    setNewRoundName('');
    setSkipQuestions(false);
    setRoundQuestions([...DEFAULT_QUESTIONS]);
    setComparisonMode('full');
    setAccuracy(70);
    setIsGrouped(false);
    setStep(1);
    setSearchTerm('');
    setDebouncedSearch('');
    setSelectedManager('');
    setManagers([]);
    setPage(1);
    setPagedResult(null);
    setSelectedEmployeeIDs(new Set());
    setEmployeeCache(new Map());
    setSelectedSearchTerm('');
    setTotalEmployeeCount(0);

    setEvalSearchTerm('');
    setEvalDebouncedSearch('');
    setEvalPage(1);
    setEvalPagedResult(null);
    setSelectedEvaluators(new Map());
    setEvaluatorCache(new Map());
    setEvalSelectedSearchTerm('');
    setEvalSelectedManager('');
    setCurrentUserEmployee(null);
    setParticipantManagerIds(new Set());
    setFinalReviewerId(null);
    setFrSelectionCount(null);
    setEvaluatorGroupMappings(new Map());
    setStep4Employees([]);
    setEnableSecondary(false);
    setPerPrimarySecondaries(new Map());
    setSecondaryStepEmployees([]);
    setStep5AllEmployees([]);
    setStep5OpenDropdown(null);
    setStep5SearchTerm('');
    setStep5ExpandedCards(new Set());
    allEmployeesCache.current = null;
    step3ParticipantSnapshot.current = null;
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // The secondary step number depends on whether grouped mode is active
  const hasSinglePrimary = isGrouped && Array.from(selectedEvaluators.keys()).filter(id => id !== finalReviewerId).length <= 1;
  const secondaryStepNumber = isGrouped ? 5 : 4;
  // Whether we need a secondary step at all
  const hasSecondaryStep = enableSecondary;

  // Get the employees assigned to a primary evaluator
  const getEmployeesForPrimary = (primaryId: number): number[] => {
    if (isGrouped) {
      // Grouped mode: use the group mapping
      const group = evaluatorGroupMappings.get(primaryId);
      return group ? Array.from(group) : [];
    }
    // Non-grouped: all selected employees
    return Array.from(selectedEmployeeIDs);
  };

  const handleBack = () => {
    if (step === secondaryStepNumber && hasSecondaryStep) {
      if (isGrouped) {
        // Skip step 4 if only 1 primary evaluator
        const primaryCount = Array.from(selectedEvaluators.keys()).filter(id => id !== finalReviewerId).length;
        setStep(primaryCount <= 1 ? 3 : 4);
      } else {
        setStep(3);
      }
      return;
    }
    if (step === 4) {
      setStep(3);
      return;
    }
    if (step === 3) {
      setStep(2);
      setEvalSearchTerm('');
      setEvalDebouncedSearch('');
      setEvalSelectedManager('');
      setEvalPage(1);
      setCurrentUserEmployee(null);
      return;
    }
    setStep(1);
    setSearchTerm('');
    setDebouncedSearch('');
    setSelectedManager('');
    setPage(1);
  };

  const handleGoToStep = (target: 1 | 2 | 3 | 4 | 5) => {
    if (target >= step) return; // only allow navigating to completed steps
    if (target <= 1) {
      setStep(1);
      setSearchTerm('');
      setDebouncedSearch('');
      setSelectedManager('');
      setPage(1);
    } else if (target === 2) {
      setStep(2);
      setEvalSearchTerm('');
      setEvalDebouncedSearch('');
      setEvalSelectedManager('');
      setEvalPage(1);
      setCurrentUserEmployee(null);
    } else {
      setStep(target);
    }
  };

  // Go to secondary step from either step 3 (non-grouped) or step 4 (grouped)
  const handleGoToSecondaryStep = () => {
    setStep(secondaryStepNumber);
  };

  // Transition from Step 3 → Step 4: reconcile the mapping with current evaluator selection.
  // FR is excluded from the mapping — they receive calibration pairs automatically in Phase 2.
  const handleGoToStep4 = () => {
    const evalIds = new Set(selectedEvaluators.keys());
    const next = new Map<number, Set<number>>(evaluatorGroupMappings);
    // Remove evaluators that were deselected or are the FR
    for (const id of next.keys()) {
      if (!evalIds.has(id) || id === finalReviewerId) next.delete(id);
    }
    // Add newly selected non-FR evaluators with empty sets
    for (const id of evalIds) {
      if (!next.has(id) && id !== finalReviewerId) next.set(id, new Set());
    }

    // If only 1 primary evaluator, auto-assign all employees to them (excluding self)
    const primaryIds = Array.from(evalIds).filter(id => id !== finalReviewerId);
    if (primaryIds.length === 1) {
      const empSet = new Set(selectedEmployeeIDs);
      empSet.delete(primaryIds[0]); // Cannot evaluate yourself
      next.set(primaryIds[0], empSet);
    }

    setEvaluatorGroupMappings(next);
    if (primaryIds.length === 1 && enableSecondary) {
      setStep(5); // Skip step 4 (Groups), go directly to step 5 (Secondary)
    } else if (primaryIds.length === 1) {
      // Single primary — no group assignment needed, create directly
      // Pass mappings directly since state update is async
      handleCreate(next);
    } else {
      setStep(4);
    }
  };

  // Step 4 helpers
  const getAssignedEvaluatorId = (employeeId: number): number | null => {
    for (const [evalId, empSet] of evaluatorGroupMappings) {
      if (empSet.has(employeeId)) return evalId;
    }
    return null;
  };

  const handleAssignEmployee = (employeeId: number, evaluatorId: number | null) => {
    setEvaluatorGroupMappings(prev => {
      const next = new Map(prev);
      // Remove from any current group
      for (const [, empSet] of next) empSet.delete(employeeId);
      // Assign to new group
      if (evaluatorId !== null) next.get(evaluatorId)?.add(employeeId);
      return next;
    });
  };

  const handleAutoAssign = () => {
    const evalIds = Array.from(selectedEvaluators.keys()).filter(id => id !== finalReviewerId);
    const next = new Map<number, Set<number>>();
    evalIds.forEach(id => next.set(id, new Set()));

    // Build evaluator name → ID lookup (case-insensitive) for manager matching
    const evalNameToId = new Map<string, number>();
    for (const id of evalIds) {
      const name = selectedEvaluators.get(id);
      if (name) evalNameToId.set(name.trim().toLowerCase(), id);
    }

    const assigned = new Set<number>();
    const remaining: number[] = [];

    // First pass: assign employees whose manager is one of the evaluators (skip self-assignment)
    for (const emp of step4Employees) {
      if (!selectedEmployeeIDs.has(emp.employeeID)) continue;
      const managerEvalId = emp.manager ? evalNameToId.get(emp.manager.trim().toLowerCase()) : undefined;
      if (managerEvalId !== undefined && managerEvalId !== emp.employeeID) {
        next.get(managerEvalId)!.add(emp.employeeID);
        assigned.add(emp.employeeID);
      }
    }

    // Collect unassigned employees
    for (const empId of selectedEmployeeIDs) {
      if (!assigned.has(empId)) remaining.push(empId);
    }

    // Second pass: distribute remaining employees to evaluators with fewest assignments (skip self-assignment)
    for (const empId of remaining) {
      let minEvalId: number | null = null;
      let minCount = Infinity;
      for (const id of evalIds) {
        if (id === empId) continue; // Cannot evaluate yourself
        const count = next.get(id)!.size;
        if (count < minCount) {
          minCount = count;
          minEvalId = id;
        }
      }
      if (minEvalId !== null) next.get(minEvalId)!.add(empId);
    }

    setEvaluatorGroupMappings(next);
  };

  // Block structure info — drives the accuracy slider step so each position = unique degree.
  // Must be before the early return so React Hook order is stable.
  const blockInfo = useMemo(
    () => {
      const empCount = selectedEmployeeIDs.size;
      const evalCount = selectedEvaluators.size;
      return comparisonMode === 'partial' && empCount >= 2 && evalCount >= 1
        ? getBlockInfo(empCount, evalCount, isGrouped)
        : null;
    },
    [comparisonMode, selectedEmployeeIDs.size, selectedEvaluators.size, isGrouped]
  );

  // Snap accuracy to the nearest valid degree level whenever maxDegree changes.
  useEffect(() => {
    if (!blockInfo || blockInfo.usesFullFallback) return;
    const { maxDegree } = blockInfo;
    setAccuracy(prev => {
      if (prev >= 100) return 100;
      // Clamp to 1..maxDegree-1 (maxDegree itself = full round-robin = 100)
      const degree = Math.max(1, Math.min(maxDegree - 1, Math.round((prev / 100) * maxDegree)));
      return Math.round((degree / maxDegree) * 100);
    });
  }, [blockInfo?.maxDegree]); // eslint-disable-line react-hooks/exhaustive-deps

  // Right-panel: selected employees from cache, filtered by client-side search
  const selectedEmployeesList = useMemo(() => {
    const items: { employeeID: number; name: string; photoURL: string; title: string; department: string }[] = [];
    for (const id of selectedEmployeeIDs) {
      const cached = employeeCache.get(id);
      if (cached) items.push({ employeeID: id, ...cached });
    }
    if (selectedSearchTerm.trim()) {
      const q = selectedSearchTerm.trim().toLowerCase();
      return items.filter(e => (e.name ?? '').toLowerCase().includes(q) || (e.title ?? '').toLowerCase().includes(q) || (e.department ?? '').toLowerCase().includes(q));
    }
    return items;
  }, [selectedEmployeeIDs, employeeCache, selectedSearchTerm]);

  // Right-panel: selected evaluators from cache, filtered by client-side search
  const selectedEvaluatorsList = useMemo(() => {
    const items: { employeeID: number; name: string; photoURL: string; title: string; department: string }[] = [];
    for (const [id] of selectedEvaluators) {
      const cached = evaluatorCache.get(id);
      if (cached) items.push({ employeeID: id, ...cached });
    }
    if (evalSelectedSearchTerm.trim()) {
      const q = evalSelectedSearchTerm.trim().toLowerCase();
      return items.filter(e => (e.name ?? '').toLowerCase().includes(q) || (e.title ?? '').toLowerCase().includes(q) || (e.department ?? '').toLowerCase().includes(q));
    }
    return items;
  }, [selectedEvaluators, evaluatorCache, evalSelectedSearchTerm]);

  // Evaluator candidates from server-paged results
  // Pin current user at top of first page when no filters are active
  const evalCandidates = useMemo((): Employee[] => {
    const items = evalPagedResult?.items ?? [];
    if (evalPage === 1 && !evalDebouncedSearch.trim() && !evalSelectedManager && currentUserEmployee && !selectedEmployeeIDs.has(currentUserEmployee.employeeID)) {
      const withoutCurrent = items.filter(e => e.employeeID !== currentUserEmployee.employeeID);
      return [currentUserEmployee, ...withoutCurrent];
    }
    return items;
  }, [evalPagedResult, evalPage, evalDebouncedSearch, evalSelectedManager, currentUserEmployee, selectedEmployeeIDs]);

  // Detect participant-managers: evaluators who are also participants (cannot evaluate themselves)
  const participantEvaluatorIds = useMemo(() => {
    const ids: number[] = [];
    for (const evalId of selectedEvaluators.keys()) {
      if (selectedEmployeeIDs.has(evalId)) ids.push(evalId);
    }
    return ids;
  }, [selectedEvaluators, selectedEmployeeIDs]);

  if (!isOpen) return null;

  const isStep1Valid = newRoundName.trim() && (
    skipQuestions || roundQuestions.every(q => q.text.trim())
  );

  const selectedCount = selectedEmployeeIDs.size;
  const isStep2Valid = selectedCount >= 2;

  const evaluatorCount = selectedEvaluators.size;
  // When grouped: need at least 1 primary evaluator + 1 FR = minimum 2 evaluators total
  const primaryCount = isGrouped ? Array.from(selectedEvaluators.keys()).filter(id => id !== finalReviewerId).length : 0;
  // For non-grouped: need at least 1 evaluator who is NOT a participant (to handle self-evaluations)
  // For grouped: need at least 1 non-participant-evaluator primary + valid FR (FR cannot be a participant)
  const hasNonParticipantEvaluator = Array.from(selectedEvaluators.keys()).some(id => !selectedEmployeeIDs.has(id) && id !== finalReviewerId);
  const frIsParticipant = finalReviewerId !== null && selectedEmployeeIDs.has(finalReviewerId);
  // Self-eval conflict only when there's exactly 1 participant-evaluator and no other evaluator to cover them
  const hasSelfEvalConflict = participantEvaluatorIds.length === 1 && !hasNonParticipantEvaluator;
  const isStep3Valid = isGrouped
    ? evaluatorCount >= 2 && finalReviewerId !== null && !frIsParticipant && primaryCount >= 1 && !hasSelfEvalConflict
    : evaluatorCount >= 1 && !hasSelfEvalConflict;

  const unassignedEmployees = Array.from(selectedEmployeeIDs).filter(
    id => getAssignedEvaluatorId(id) === null
  );
  const isStep4Valid = unassignedEmployees.length === 0;

  const employees = pagedResult?.items ?? [];
  const totalPages = pagedResult?.totalPages ?? 0;

  const evalTotalPages = evalPagedResult?.totalPages ?? 1;
  const evalEmployees = evalCandidates;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 max-w-5xl w-full max-h-[95vh] flex flex-col border border-gray-200" style={{ overflow: 'hidden' }}>
        {/* Header */}
        {/* Header: title on step 1, compact back/stepper/close on steps 2-4 */}
        {step === 1 ? (
          <div className="flex items-center justify-between mb-4 shrink-0">
            <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Create New Evaluation Round
            </h3>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        ) : null}

        {/* Step indicators — scrollable on mobile */}
        <div className="flex items-center gap-1 sm:gap-3 mb-3 sm:mb-5 shrink-0">
          {step > 1 && (
            <button
              onClick={handleBack}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
          {/* Step indicators: on mobile, hide labels and show only numbered circles */}
          <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0 justify-center sm:justify-start">
            <button
              onClick={() => handleGoToStep(1)}
              disabled={step <= 1}
              className={`flex shrink-0 items-center gap-1.5 sm:px-2.5 px-1 py-1 rounded-full text-xs font-medium transition-colors ${step === 1 ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200'}`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === 1 ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'}`}>1</span>
              <span className="hidden sm:inline">Setup</span>
            </button>
            <div className="shrink-0 w-3 sm:w-6 h-0.5 bg-gray-300"></div>
            <button
              onClick={() => handleGoToStep(2)}
              disabled={step <= 2}
              className={`flex shrink-0 items-center gap-1.5 sm:px-2.5 px-1 py-1 rounded-full text-xs font-medium transition-colors ${step === 2 ? 'bg-blue-100 text-blue-700' : step > 2 ? 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200' : 'bg-gray-100 text-gray-500'}`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === 2 ? 'bg-blue-600 text-white' : step > 2 ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>2</span>
              <span className="hidden sm:inline">Employees</span>
            </button>
            <div className="shrink-0 w-3 sm:w-6 h-0.5 bg-gray-300"></div>
            <button
              onClick={() => handleGoToStep(3)}
              disabled={step <= 3}
              className={`flex shrink-0 items-center gap-1.5 sm:px-2.5 px-1 py-1 rounded-full text-xs font-medium transition-colors ${step === 3 ? 'bg-blue-100 text-blue-700' : step > 3 ? 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200' : 'bg-gray-100 text-gray-500'}`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === 3 ? 'bg-blue-600 text-white' : step > 3 ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>3</span>
              <span className="hidden sm:inline">Evaluators</span>
            </button>
            {isGrouped && Array.from(selectedEvaluators.keys()).filter(id => id !== finalReviewerId).length > 1 && (
              <>
                <div className="shrink-0 w-3 sm:w-6 h-0.5 bg-gray-300"></div>
                <button
                  disabled
                  className={`flex shrink-0 items-center gap-1.5 sm:px-2.5 px-1 py-1 rounded-full text-xs font-medium ${step === 4 ? 'bg-blue-100 text-blue-700' : step > 4 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === 4 ? 'bg-blue-600 text-white' : step > 4 ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'}`}>4</span>
                  <span className="hidden sm:inline">Groups</span>
                </button>
              </>
            )}
            {hasSecondaryStep && (
              <>
                <div className="shrink-0 w-3 sm:w-6 h-0.5 bg-gray-300"></div>
                <button
                  disabled
                  className={`flex shrink-0 items-center gap-1.5 sm:px-2.5 px-1 py-1 rounded-full text-xs font-medium ${step === secondaryStepNumber ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === secondaryStepNumber ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-gray-600'}`}>{hasSinglePrimary ? 4 : secondaryStepNumber}</span>
                  <span className="hidden sm:inline">Secondary</span>
                </button>
              </>
            )}
          </div>
          {step > 1 && (
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>

        {/* Step 1: Round Configuration — scrollable on mobile */}
        {step === 1 && (
          <div className="space-y-6 flex-1 min-h-0" style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                Round Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newRoundName}
                onChange={(e) => setNewRoundName(e.target.value)}
                maxLength={50}
                placeholder="e.g., Q1 2026 Review, ENG-Q1-2026"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium shadow-sm"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{newRoundName.length}/50</p>
            </div>

            <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <input
                type="checkbox"
                id="skipQuestions"
                checked={skipQuestions}
                onChange={(e) => setSkipQuestions(e.target.checked)}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="skipQuestions" className="text-sm font-medium text-gray-900 cursor-pointer">
                Create round without questions (simplified comparison mode)
              </label>
            </div>

            {skipQuestions && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-gray-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  This round will use simple A vs B comparisons. Rankings may be less nuanced.
                </p>
              </div>
            )}

            {/* Comparison Mode */}
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3">
                Comparison Mode <span className="text-red-500">*</span>
              </label>
              {/* Responsive: comparison mode cards stack on small mobile */}
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
                {/* Full mode card */}
                <button
                  type="button"
                  onClick={() => setComparisonMode('full')}
                  className={`relative flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${comparisonMode === 'full'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40'
                    }`}
                >
                  {comparisonMode === 'full' && (
                    <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="w-2 h-2 rounded-full bg-white" />
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <LayoutGrid className={`w-5 h-5 ${comparisonMode === 'full' ? 'text-blue-600' : 'text-gray-500'}`} />
                    <span className={`text-sm font-bold ${comparisonMode === 'full' ? 'text-blue-700' : 'text-gray-700'}`}>
                      Full Comparison
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Every possible pair is compared (N×(N−1)/2). Best for small groups or when you want maximum accuracy.
                  </p>
                </button>

                {/* Partial mode card */}
                <button
                  type="button"
                  onClick={() => setComparisonMode('partial')}
                  className={`relative flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${comparisonMode === 'partial'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/40'
                    }`}
                >
                  {comparisonMode === 'partial' && (
                    <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-purple-600 flex items-center justify-center">
                      <span className="w-2 h-2 rounded-full bg-white" />
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <GitBranch className={`w-5 h-5 ${comparisonMode === 'partial' ? 'text-purple-600' : 'text-gray-500'}`} />
                    <span className={`text-sm font-bold ${comparisonMode === 'partial' ? 'text-purple-700' : 'text-gray-700'}`}>
                      Partial Comparison
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Block-overlap structure — ~80% fewer comparisons while keeping the graph connected. Ideal for large groups.
                  </p>
                </button>
              </div>
            </div>

            {/* Grouped mapping toggle */}
            <div
              onClick={() => setIsGrouped(v => !v)}
              className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${isGrouped ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/40'
                }`}
            >
              <div className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center shrink-0 border-2 transition-colors ${isGrouped ? 'bg-amber-500 border-amber-500' : 'border-gray-300 bg-white'
                }`}>
                {isGrouped && <span className="text-white text-xs font-bold">✓</span>}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Crown className={`w-4 h-4 ${isGrouped ? 'text-amber-600' : 'text-gray-400'}`} />
                  <span className={`text-sm font-bold ${isGrouped ? 'text-amber-800' : 'text-gray-700'}`}>
                    Enable Grouped Evaluator Mapping
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Assign each evaluator an exclusive group of employees. A Final Reviewer then runs cross-group calibration comparisons for the top performers.
                </p>
              </div>
            </div>

            {!skipQuestions && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-bold text-gray-900">
                    Questions <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={handleResetToDefaults}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:scale-95 transition-all duration-150 shadow-sm hover:shadow-md font-medium border border-gray-300 cursor-pointer"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset to Defaults
                    </button>
                    <button
                      onClick={addQuestion}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 active:scale-95 transition-all duration-150 shadow-md hover:shadow-lg font-medium cursor-pointer"
                    >
                      <Plus className="w-4 h-4" />
                      Add Question
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {roundQuestions.map((q, idx) => (
                    <div
                      key={idx}
                      className="flex gap-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200"
                    >
                      <span className="flex items-center justify-center w-8 h-10 text-sm font-bold text-white bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg shadow-sm">
                        {idx + 1}
                      </span>
                      <input
                        type="text"
                        value={q.text}
                        onChange={(e) => updateQuestion(idx, e.target.value)}
                        placeholder="Enter question text"
                        className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                      />
                      {roundQuestions.length > 1 && (
                        <button
                          onClick={() => removeQuestion(idx)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-600 mt-3 flex items-center gap-2 bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                  Users must answer all questions before completing a comparison
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t-2 border-gray-200">
              <button
                onClick={() => setStep(2)}
                disabled={!isStep1Valid}
                className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition-all shadow-lg ${!isStep1Valid
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 hover:shadow-xl cursor-pointer'
                  }`}
              >
                Next
              </button>
              <button
                onClick={handleClose}
                className="flex-1 px-6 py-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-bold text-lg shadow-md hover:shadow-lg cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Employee Selection — Dual-Pane Transfer List */}
        {step === 2 && (
          <div className="flex flex-col flex-1 min-h-0 gap-4" style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {/* Responsive: panels stack on mobile, side-by-side on md+ */}
            <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0">
              {/* ── Left Panel: Available Employees ── */}
              <div className="flex-[3] flex flex-col min-w-0 border border-gray-200 rounded-lg overflow-hidden min-h-[200px] md:min-h-0">
                {/* Left header */}
                <div className="px-3 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Available Employees</h4>
                    <button
                      onClick={handleSelectAll}
                      disabled={selectAllLoading}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors font-medium border border-blue-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {selectAllLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-700"></div>
                          Selecting...
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-3 h-3" />
                          Select All
                        </>
                      )}
                    </button>
                  </div>
                  {/* Filters */}
                  <div className="flex items-center gap-2">
                    <div className="relative min-w-[160px]">
                      <select
                        value={selectedManager}
                        onChange={(e) => setSelectedManager(e.target.value)}
                        disabled={managersLoading}
                        className="appearance-none w-full pl-3 pr-7 py-1.5 bg-white border border-gray-300 rounded text-xs font-medium text-gray-700 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">All Managers</option>
                        {managers.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                    </div>
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-7 pr-7 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {searchTerm && (
                        <button type="button" onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Left list */}
                <div className="flex-1 overflow-y-auto">
                  {employeesLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-gray-500 text-xs">Loading...</p>
                    </div>
                  ) : employees.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {employees.map(emp => {
                        const selected = selectedEmployeeIDs.has(emp.employeeID);
                        return (
                          <div
                            key={emp.employeeID}
                            onClick={() => toggleEmployee(emp.employeeID)}
                            className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${selected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                              }`}
                          >
                            {selected ? (
                              <CheckSquare className="w-4 h-4 text-blue-600 shrink-0" />
                            ) : (
                              <Square className="w-4 h-4 text-gray-400 shrink-0" />
                            )}
                            <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="small" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                              <p className="text-xs text-gray-500 truncate">{emp.title}{emp.department ? ` · ${emp.department}` : ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-xs">
                        {searchTerm || selectedManager ? 'No matching employees' : 'No employees found'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Left footer: pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="flex items-center gap-1 px-2 py-1 rounded bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium border border-gray-200 cursor-pointer"
                    >
                      <ChevronLeft className="w-3 h-3" /> Prev
                    </button>
                    <span className="text-gray-600 text-xs">{page} / {totalPages}</span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="flex items-center gap-1 px-2 py-1 rounded bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium border border-gray-200 cursor-pointer"
                    >
                      Next <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>

              {/* ── Right Panel: Selected Employees ── */}
              <div className="flex-[2] flex flex-col min-w-0 border-2 border-blue-200 rounded-lg overflow-hidden bg-blue-50/30 min-h-[200px] md:min-h-0">
                {/* Right header */}
                <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-blue-800 uppercase tracking-wider flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />
                      Selected ({selectedCount})
                    </h4>
                    {selectedCount > 0 && (
                      <button
                        onClick={() => {
                          if (selectedSearchTerm.trim()) {
                            // Remove only the filtered (visible) items
                            const idsToRemove = new Set(selectedEmployeesList.map(e => e.employeeID));
                            const next = new Set(selectedEmployeeIDs);
                            for (const id of idsToRemove) next.delete(id);
                            setSelectedEmployeeIDs(next);
                            setSelectedSearchTerm('');
                          } else {
                            handleDeselectAll();
                          }
                        }}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-white text-red-600 rounded hover:bg-red-50 transition-colors font-medium border border-red-200 cursor-pointer"
                      >
                        <UserMinus className="w-3 h-3" />
                        {selectedSearchTerm.trim() ? `Remove ${selectedEmployeesList.length} Filtered` : 'Remove All'}
                      </button>
                    )}
                  </div>
                  {selectedCount > 5 && (
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Filter selected..."
                        value={selectedSearchTerm}
                        onChange={(e) => setSelectedSearchTerm(e.target.value)}
                        className="w-full pl-7 pr-7 py-1.5 border border-blue-200 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      />
                      {selectedSearchTerm && (
                        <button type="button" onClick={() => setSelectedSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Right list */}
                <div className="flex-1 overflow-y-auto">
                  {selectedCount === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-10 h-10 text-blue-200 mx-auto mb-2" />
                      <p className="text-blue-400 text-xs font-medium">No employees selected</p>
                      <p className="text-blue-300 text-xs mt-1">Click employees on the left to add them</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-blue-100">
                      {selectedEmployeesList.map(emp => (
                        <div
                          key={emp.employeeID}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 transition-colors group"
                        >
                          <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="small" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                            <p className="text-xs text-gray-500 truncate">{emp.title}{emp.department ? ` · ${emp.department}` : ''}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const next = new Set(selectedEmployeeIDs);
                              next.delete(emp.employeeID);
                              setSelectedEmployeeIDs(next);
                            }}
                            className="p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                            title="Remove"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right footer: validation */}
                <div className="px-3 py-2 border-t border-blue-200 bg-blue-50">
                  <p className={`text-xs font-medium ${employeesLoading && selectedCount === 0 ? 'text-blue-700' : selectedCount < 2 ? 'text-red-600' : 'text-blue-700'}`}>
                    {selectedCount} of {totalEmployeeCount} selected
                    {!(employeesLoading && selectedCount === 0) && selectedCount < 2 && ' — minimum 2 required'}
                  </p>
                </div>
              </div>
            </div>



            {/* Action buttons */}
            <div className="flex gap-3 pt-4 border-t-2 border-gray-200 shrink-0">
              <button
                onClick={() => setStep(3)}
                disabled={!isStep2Valid}
                className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition-all shadow-lg ${!isStep2Valid
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 hover:shadow-xl cursor-pointer'
                  }`}
              >
                Next ({selectedCount} employees)
              </button>
              <button
                onClick={handleBack}
                className="px-6 py-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-bold text-lg shadow-md hover:shadow-lg cursor-pointer"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Evaluator Selection — Dual-Pane Transfer List */}
        {step === 3 && (
          <div className="flex flex-col flex-1 min-h-0 gap-2" style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {/* Accuracy slider + estimate — partial mode only */}
            {comparisonMode === 'partial' && (() => {
              const maxDegree = blockInfo && !blockInfo.usesFullFallback ? blockInfo.maxDegree : 0;
              const currentDegree = maxDegree > 0
                ? (accuracy >= 100 ? maxDegree : Math.max(1, Math.min(maxDegree - 1, Math.round((accuracy / 100) * maxDegree))))
                : 1;
              const isFullMode = maxDegree > 0 && currentDegree >= maxDegree;
              const displayPct = isFullMode ? 100 : (maxDegree > 0 ? Math.round((currentDegree / maxDegree) * 100) : accuracy);
              const handleSliderChange = (pct: number) => {
                if (maxDegree <= 0) return;
                const degree = Math.max(1, Math.min(maxDegree, Math.round((pct / 100) * maxDegree)));
                setAccuracy(degree >= maxDegree ? 100 : Math.round((degree / maxDegree) * 100));
              };
              const partialLevels = maxDegree - 1;
              const levelLabel = isFullMode ? 'Full'
                : partialLevels > 0 && currentDegree <= Math.ceil(partialLevels * 0.33) ? 'Low'
                  : partialLevels > 0 && currentDegree <= Math.ceil(partialLevels * 0.66) ? 'Medium' : 'High';
              const badgeColor = levelLabel === 'Full' || levelLabel === 'High'
                ? 'bg-green-100 text-green-700'
                : levelLabel === 'Medium' ? 'bg-blue-100 text-blue-700'
                  : 'bg-yellow-100 text-yellow-800';
              const est = selectedCount >= 2 && evaluatorCount >= 1
                ? getEstimatedComparisons(selectedCount, comparisonMode, evaluatorCount, accuracy, isGrouped) : null;
              const fullEst = est ? getEstimatedComparisons(selectedCount, 'full', isGrouped ? evaluatorCount : 1, 70, isGrouped) : null;
              const savings = est && fullEst ? Math.round((1 - est.perEvaluator / fullEst.perEvaluator) * 100) : 0;
              const sliderDisabled = maxDegree <= 0;
              return (
                <div className="px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs font-bold text-purple-900">Accuracy</span>
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${badgeColor}`}>
                        {levelLabel} · {displayPct}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={sliderDisabled ? 70 : displayPct}
                      onChange={e => handleSliderChange(Number(e.target.value))}
                      disabled={sliderDisabled}
                      className="flex-1 accent-purple-600 disabled:opacity-40"
                    />
                    {est && (
                      <span className="text-xs text-purple-700 shrink-0 w-52 text-right">
                        ~<strong>{est.perEvaluator}</strong>/eval {savings > 0 ? `(${savings}% fewer)` : ''}
                        {` · ${evaluatorCount} eval × ${selectedCount} emp`}
                      </span>
                    )}
                  </div>
                  {levelLabel === 'Low' && (
                    <p className="text-[10px] text-yellow-700 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      Low accuracy may produce less reliable rankings.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Comparison estimate — full mode only */}
            {comparisonMode === 'full' && selectedCount >= 2 && evaluatorCount >= 1 && (() => {
              const est = getEstimatedComparisons(selectedCount, comparisonMode, evaluatorCount, accuracy, isGrouped);
              return (
                <div className="px-3 py-2 rounded-lg border text-xs flex items-center gap-2 bg-blue-50 border-blue-200 shrink-0">
                  <LayoutGrid className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  <span className="text-blue-700">
                    <strong>{est.perEvaluator}</strong> comparisons/eval · {evaluatorCount} evaluator{evaluatorCount !== 1 ? 's' : ''} × {selectedCount} employees
                  </span>
                </div>
              );
            })()}

            {isGrouped && (
              <div className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-xs text-amber-800 shrink-0">
                <Crown className="w-3.5 h-3.5 shrink-0 text-amber-600" />
                <span>Select evaluators, then designate one as <strong>Final Reviewer</strong> (crown icon). FR evaluates across groups after Phase 1.</span>
              </div>
            )}

            {/* Responsive: evaluator panels stack on mobile */}
            <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0">
              {/* ── Left Panel: Available Evaluators ── */}
              <div className="flex-[3] flex flex-col min-w-0 border border-gray-200 rounded-lg overflow-hidden min-h-[200px] md:min-h-0">
                {/* Left header */}
                <div className="px-3 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Available Evaluators</h4>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={handleSelectAllEvaluators}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors font-medium border border-blue-200 cursor-pointer"
                      >
                        <UserPlus className="w-3 h-3" />
                        Select All
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative min-w-[160px]">
                      <select
                        value={evalSelectedManager}
                        onChange={(e) => setEvalSelectedManager(e.target.value)}
                        disabled={managersLoading}
                        className="appearance-none w-full pl-3 pr-7 py-1.5 bg-white border border-gray-300 rounded text-xs font-medium text-gray-700 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">All Managers</option>
                        {managers.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                    </div>
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search evaluators..."
                        value={evalSearchTerm}
                        onChange={(e) => setEvalSearchTerm(e.target.value)}
                        className="w-full pl-7 pr-7 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {evalSearchTerm && (
                        <button type="button" onClick={() => setEvalSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Left list */}
                <div className="flex-1 overflow-y-auto">
                  {evalEmployeesLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-gray-500 text-xs">Loading...</p>
                    </div>
                  ) : evalEmployees.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {evalEmployees.map(emp => {
                        const selected = selectedEvaluators.has(emp.employeeID);
                        const isCurrentUser = currentUserId !== undefined && currentUserId > 0 && emp.employeeID === currentUserId;
                        return (
                          <div
                            key={emp.employeeID}
                            onClick={() => toggleEvaluator(emp)}
                            className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${selected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                              }`}
                          >
                            {selected ? (
                              <CheckSquare className="w-4 h-4 text-blue-600 shrink-0" />
                            ) : (
                              <Square className="w-4 h-4 text-gray-400 shrink-0" />
                            )}
                            <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="small" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                                {isCurrentUser && (
                                  <span className="px-1.5 py-0.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-[10px] font-bold rounded-full shrink-0 leading-none">Me</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 truncate">{emp.title}{emp.department ? ` · ${emp.department}` : ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-xs">
                        {evalSearchTerm || evalSelectedManager ? 'No matching evaluators' : 'No employees found'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Left footer: pagination */}
                {evalTotalPages > 1 && (
                  <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50">
                    <button
                      onClick={() => setEvalPage(p => Math.max(1, p - 1))}
                      disabled={evalPage === 1}
                      className="flex items-center gap-1 px-2 py-1 rounded bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium border border-gray-200 cursor-pointer"
                    >
                      <ChevronLeft className="w-3 h-3" /> Prev
                    </button>
                    <span className="text-gray-600 text-xs">{evalPage} / {evalTotalPages}</span>
                    <button
                      onClick={() => setEvalPage(p => Math.min(evalTotalPages, p + 1))}
                      disabled={evalPage === evalTotalPages}
                      className="flex items-center gap-1 px-2 py-1 rounded bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium border border-gray-200 cursor-pointer"
                    >
                      Next <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>

              {/* ── Right Panel: Selected Evaluators ── */}
              <div className="flex-[2] flex flex-col min-w-0 border-2 border-blue-200 rounded-lg overflow-hidden bg-blue-50/30 min-h-[200px] md:min-h-0">
                {/* Right header */}
                <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-blue-800 uppercase tracking-wider flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />
                      Selected ({evaluatorCount})
                    </h4>
                    {evaluatorCount > 0 && (
                      <button
                        onClick={() => {
                          if (evalSelectedSearchTerm.trim()) {
                            // Remove only the filtered (visible) evaluators
                            const idsToRemove = new Set(selectedEvaluatorsList.map(e => e.employeeID));
                            const next = new Map(selectedEvaluators);
                            for (const id of idsToRemove) {
                              next.delete(id);
                              if (finalReviewerId === id) setFinalReviewerId(null);
                            }
                            setSelectedEvaluators(next);
                            setEvalSelectedSearchTerm('');
                          } else {
                            handleDeselectAllEvaluators();
                          }
                        }}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-white text-red-600 rounded hover:bg-red-50 transition-colors font-medium border border-red-200 cursor-pointer"
                      >
                        <UserMinus className="w-3 h-3" />
                        {evalSelectedSearchTerm.trim() ? `Remove ${selectedEvaluatorsList.length} Filtered` : 'Remove All'}
                      </button>
                    )}
                  </div>
                  {evaluatorCount > 5 && (
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Filter selected..."
                        value={evalSelectedSearchTerm}
                        onChange={(e) => setEvalSelectedSearchTerm(e.target.value)}
                        className="w-full pl-7 pr-7 py-1.5 border border-blue-200 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      />
                      {evalSelectedSearchTerm && (
                        <button type="button" onClick={() => setEvalSelectedSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Right list */}
                <div className="flex-1 overflow-y-auto">
                  {evaluatorCount === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-10 h-10 text-blue-200 mx-auto mb-2" />
                      <p className="text-blue-400 text-xs font-medium">No evaluators selected</p>
                      <p className="text-blue-300 text-xs mt-1">Click employees on the left to add them</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-blue-100">
                      {selectedEvaluatorsList.map(emp => {
                        const isFR = finalReviewerId === emp.employeeID;
                        const isParticipantEval = selectedEmployeeIDs.has(emp.employeeID);
                        return (
                          <div
                            key={emp.employeeID}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 transition-colors group"
                          >
                            <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="small" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                                {isFR && (
                                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full shrink-0 leading-none">FR</span>
                                )}
                                {isParticipantEval && (
                                  <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 text-[10px] font-bold rounded-full shrink-0 leading-none">Participant</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 truncate">{emp.title}{emp.department ? ` · ${emp.department}` : ''}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {isGrouped && (
                                <button
                                  type="button"
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (isParticipantEval) return; // Participants cannot be FR
                                    setFinalReviewerId(isFR ? null : emp.employeeID);
                                  }}
                                  title={isParticipantEval ? 'Participants cannot be Final Reviewer' : isFR ? 'Remove Final Reviewer' : 'Set as Final Reviewer'}
                                  className={`p-1 rounded-full transition-colors ${isParticipantEval
                                    ? 'text-gray-200 cursor-not-allowed'
                                    : isFR
                                      ? 'text-amber-500 bg-amber-100 hover:bg-amber-200 cursor-pointer'
                                      : 'text-gray-300 hover:text-amber-400 hover:bg-amber-50 cursor-pointer'
                                    }`}
                                >
                                  <Crown className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  const next = new Map(selectedEvaluators);
                                  next.delete(emp.employeeID);
                                  if (finalReviewerId === emp.employeeID) setFinalReviewerId(null);
                                  setSelectedEvaluators(next);
                                }}
                                className="p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                                title="Remove"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Right footer: validation */}
                <div className="px-3 py-2 border-t border-blue-200 bg-blue-50">
                  <p className={`text-xs font-medium ${evalEmployeesLoading && evaluatorCount === 0 ? 'text-blue-700' : !isStep3Valid ? 'text-red-600' : 'text-blue-700'}`}>
                    {frIsParticipant
                      ? `${evaluatorCount} selected — FR cannot be a participant, reassign FR`
                      : hasSelfEvalConflict
                        ? `${evaluatorCount} selected — add an evaluator who isn't a participant`
                        : isGrouped && finalReviewerId && primaryCount < 1
                          ? 'FR selected — add at least 1 evaluator'
                          : <>{evaluatorCount} evaluator{evaluatorCount !== 1 ? 's' : ''} selected
                            {!(evalEmployeesLoading && evaluatorCount === 0) && !isGrouped && evaluatorCount < 1 && ' — minimum 1 required'}
                            {!(evalEmployeesLoading && evaluatorCount === 0) && isGrouped && !finalReviewerId && evaluatorCount === 0 && ' — need 1 evaluator + 1 Final Reviewer'}
                            {isGrouped && !finalReviewerId && evaluatorCount === 1 && ' — assign a Final Reviewer'}
                            {isGrouped && !finalReviewerId && evaluatorCount >= 2 && ' — assign a Final Reviewer'}
                          </>
                    }
                  </p>
                </div>

                {/* FR selection count override — only when grouped + FR designated */}
                {isGrouped && finalReviewerId && (
                  <div className="px-3 py-2 border-t border-amber-200 bg-amber-50">
                    <label className="block text-xs font-medium text-amber-800 mb-1">
                      Employees per group for Final Review
                    </label>
                    <input
                      type="number"
                      min={2}
                      placeholder="Auto (20% of group, capped 2–5)"
                      value={frSelectionCount ?? ''}
                      onChange={e => {
                        const v = e.target.value;
                        setFrSelectionCount(v === '' ? null : Math.max(2, parseInt(v, 10) || 2));
                      }}
                      className="w-full px-2 py-1 text-sm border border-amber-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
                    />
                    <p className="text-[10px] text-amber-600 mt-0.5">Leave blank for auto-calculation</p>
                  </div>
                )}
              </div>
            </div>

            {/* Secondary evaluator toggle — card-style */}
            <div
              onClick={() => {
                setEnableSecondary(v => !v);
                if (enableSecondary) { setPerPrimarySecondaries(new Map()); }
              }}
              className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all shrink-0 ${enableSecondary ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40'
                }`}
            >
              <div className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center border-2 shrink-0 ${enableSecondary ? 'bg-indigo-600 border-indigo-600' : 'border-gray-400'
                }`}>
                {enableSecondary && <span className="text-white text-xs font-bold">✓</span>}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Users className={`w-4 h-4 ${enableSecondary ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <span className={`text-sm font-bold ${enableSecondary ? 'text-indigo-800' : 'text-gray-700'}`}>
                    Enable Secondary Evaluators
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Assign secondary evaluators to each primary evaluator. They independently perform the same comparisons, with results tracked separately and both contributing to ELO.
                </p>
              </div>
            </div>

            {/* Step 3 action buttons */}
            <div className="flex gap-3 pt-4 border-t-2 border-gray-200 shrink-0">
              {isGrouped ? (
                <button
                  onClick={handleGoToStep4}
                  disabled={!isStep3Valid || loading}
                  className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition-all shadow-lg ${!isStep3Valid || loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 hover:shadow-xl cursor-pointer'
                    }`}
                >
                  {hasSinglePrimary && !enableSecondary
                    ? (loading ? 'Creating...' : `Create Round (${evaluatorCount} evaluators)`)
                    : `Next (${evaluatorCount} evaluators)`}
                </button>
              ) : enableSecondary ? (
                <button
                  onClick={handleGoToSecondaryStep}
                  disabled={!isStep3Valid}
                  className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition-all shadow-lg ${!isStep3Valid
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 hover:shadow-xl cursor-pointer'
                    }`}
                >
                  Next: Assign Secondary Evaluators
                </button>
              ) : (
                <button
                  onClick={handleCreate}
                  disabled={loading || !isStep3Valid}
                  className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition-all shadow-lg ${loading || !isStep3Valid
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 hover:shadow-xl cursor-pointer'
                    }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Creating...
                    </span>
                  ) : (
                    `Create Round (${evaluatorCount} evaluators)`
                  )}
                </button>
              )}
              <button
                onClick={handleBack}
                className="px-6 py-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-bold text-lg shadow-md hover:shadow-lg cursor-pointer"
              >
                Back
              </button>
            </div>
          </div>
        )}
        {/* Step 4: Group Mapping (only when grouped mode with multiple primary evaluators) */}
        {step === 4 && isGrouped && !hasSinglePrimary && (
          <div className="flex flex-col flex-1 min-h-0 gap-4" style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <p className="text-sm text-gray-600 shrink-0">
              Assign each employee to exactly one evaluator group. The Final Reviewer will automatically receive cross-group calibration comparisons once Phase 1 is complete.
            </p>

            {/* Toolbar */}
            <div className="flex items-center justify-between shrink-0">
              <button
                type="button"
                onClick={handleAutoAssign}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors font-medium border border-amber-200 cursor-pointer"
              >
                <Shuffle className="w-4 h-4" />
                Auto-assign
              </button>
              {/* Summary chips */}
              <div className="flex items-center gap-2 text-xs">
                {(() => {
                  const entries = Array.from(selectedEvaluators.entries()).filter(([evalId]) => evalId !== finalReviewerId);
                  // Detect duplicate first names to show disambiguated labels
                  const firstNames = entries.map(([, name]) => name.split(' ')[0]);
                  const firstNameCount = new Map<string, number>();
                  firstNames.forEach(fn => firstNameCount.set(fn, (firstNameCount.get(fn) ?? 0) + 1));
                  return entries.map(([evalId, evalName]) => {
                    const count = evaluatorGroupMappings.get(evalId)?.size ?? 0;
                    const firstName = evalName.split(' ')[0];
                    const displayName = (firstNameCount.get(firstName) ?? 0) > 1
                      ? evalName.split(' ').slice(0, 2).join(' ')
                      : firstName;
                    return (
                      <span key={evalId} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                        {displayName}: {count}
                      </span>
                    );
                  });
                })()}
                {unassignedEmployees.length > 0 && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                    {unassignedEmployees.length} unassigned
                  </span>
                )}
              </div>
            </div>

            {/* Validation warning */}
            {!isStep4Valid && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-xs text-red-700 shrink-0">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {unassignedEmployees.length} employee{unassignedEmployees.length !== 1 ? 's are' : ' is'} not assigned to any group. All employees must be assigned before creating the round.
              </div>
            )}

            {/* Employee assignment table */}
            {step4Loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-gray-500 text-sm">Loading employees...</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-y-auto flex-1 min-h-0">
                <table className="w-full table-fixed">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 sticky top-0 z-10">
                    <tr>
                      <th className="w-[35%] px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Employee</th>
                      <th className="w-[30%] px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Title</th>
                      <th className="w-[35%] px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Assigned to</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {step4Employees.map(emp => {
                      const assignedEvalId = getAssignedEvaluatorId(emp.employeeID);
                      const isUnassigned = assignedEvalId === null;
                      return (
                        <tr key={emp.employeeID} className={isUnassigned ? 'bg-red-50' : ''}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="small" />
                              <span className="font-semibold text-gray-900 text-sm truncate" title={emp.name}>{emp.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600 truncate block" title={emp.title}>{emp.title}</span>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={assignedEvalId ?? ''}
                              onChange={e => handleAssignEmployee(emp.employeeID, e.target.value ? Number(e.target.value) : null)}
                              onClick={e => e.stopPropagation()}
                              className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isUnassigned
                                ? 'border-red-300 bg-red-50 text-red-700'
                                : 'border-gray-300 bg-white text-gray-700'
                                }`}
                            >
                              <option value="">— Unassigned —</option>
                              {Array.from(selectedEvaluators.entries())
                                .filter(([evalId]) => evalId !== finalReviewerId && evalId !== emp.employeeID)
                                .map(([evalId, evalName]) => (
                                  <option key={evalId} value={evalId}>{evalName}</option>
                                ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Step 4 action buttons */}
            <div className="flex gap-3 pt-4 border-t-2 border-gray-200 shrink-0">
              {enableSecondary ? (
                <button
                  onClick={handleGoToSecondaryStep}
                  disabled={!isStep4Valid}
                  className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition-all shadow-lg ${!isStep4Valid
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 hover:shadow-xl cursor-pointer'
                    }`}
                >
                  Next: Assign Secondary Evaluators
                </button>
              ) : (
                <button
                  onClick={handleCreate}
                  disabled={loading || !isStep4Valid}
                  className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition-all shadow-lg ${loading || !isStep4Valid
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 hover:shadow-xl cursor-pointer'
                    }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Creating...
                    </span>
                  ) : (
                    `Create Round (${evaluatorCount} evaluators · ${selectedCount} employees)`
                  )}
                </button>
              )}
              <button
                onClick={handleBack}
                className="px-6 py-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-bold text-lg shadow-md hover:shadow-lg cursor-pointer"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Step 5 (or 4 for non-grouped): Secondary Evaluator Mapping — per-primary */}
        {step === secondaryStepNumber && hasSecondaryStep && (() => {
          const primaryEntries = Array.from(selectedEvaluators.entries()).filter(([id]) => id !== finalReviewerId);
          // Only count secondaries for actual primary evaluators (exclude FR)
          const totalSecondaryCount = primaryEntries.reduce((sum, [id]) => sum + (perPrimarySecondaries.get(id)?.size ?? 0), 0);
          const primaryCount = primaryEntries.length;
          const totalRoundCount = isGrouped
            ? 1 + primaryCount + totalSecondaryCount // grouped: 1 parent/FR + primary groups + secondary rounds
            : 1 + totalSecondaryCount; // non-grouped: 1 main round + secondary rounds
          const searchLower = step5SearchTerm.trim().toLowerCase();

          return (
            <div className="flex flex-col flex-1 min-h-0 gap-4" style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <p className="text-sm text-gray-600 shrink-0">
                Assign one or more secondary evaluators to each primary evaluator. They independently perform the same comparisons, with results tracked separately and both contributing to ELO.
              </p>

              {secondaryStepLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div>
                  <p className="text-gray-500 text-sm">Loading employees...</p>
                </div>
              ) : (
                <div className="flex flex-col gap-3 overflow-y-auto flex-1 min-h-0">
                  {primaryEntries.map(([primaryId, primaryName], cardIndex) => {
                    const primaryCached = evaluatorCache.get(primaryId);
                    const primaryPhoto = primaryCached?.photoURL ?? '';
                    const primaryEmpIds = getEmployeesForPrimary(primaryId);
                    const primaryEmployees = secondaryStepEmployees.filter(e => primaryEmpIds.includes(e.employeeID));
                    const thisSecIds = perPrimarySecondaries.get(primaryId) ?? new Set<number>();
                    // First card: dropdown opens downward (no room above); rest: upward
                    const dropdownAbove = cardIndex > 0;
                    const thisSecondaries = Array.from(thisSecIds).map(secId => {
                      const emp = step5AllEmployees.find(e => e.employeeID === secId);
                      const cached = evaluatorCache.get(secId);
                      return { id: secId, name: emp?.name ?? cached?.name ?? `ID ${secId}`, title: emp?.title ?? cached?.title ?? '' };
                    });
                    const isAddDropdownOpen = step5OpenDropdown === primaryId;

                    // Candidates: exclude this card's own primary, final reviewer, employees being evaluated, and already-assigned secondaries
                    // Other primaries CAN be selected as secondary for a different group
                    const primaryEmpIdSet = new Set(primaryEmpIds);
                    const filteredCandidates = step5AllEmployees.filter(e =>
                      e.employeeID !== primaryId && e.employeeID !== finalReviewerId && !primaryEmpIdSet.has(e.employeeID) && !thisSecIds.has(e.employeeID)
                    );

                    const isExpanded = step5ExpandedCards.has(primaryId);
                    const truncName = (name: string) => name.length > 15 ? name.slice(0, 15) + '...' : name;

                    return (
                      <div key={primaryId} className="border border-gray-200 rounded-xl bg-white shadow-sm">
                        {/* Row 1: Primary evaluator + badge inline */}
                        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-50 to-indigo-50 border-b border-gray-100 rounded-t-xl">
                          <EmployeePhoto photoURL={primaryPhoto} name={primaryName} size="small" />
                          <span className="text-sm font-bold text-gray-900 group relative" title={primaryName.length > 15 ? primaryName : undefined}>
                            {truncName(primaryName)}
                          </span>
                          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full shrink-0">Primary</span>
                          <span className="flex-1" />
                          <span className="text-[11px] text-gray-400 shrink-0">{primaryEmployees.length} employees</span>
                        </div>

                        {/* Row 2: Secondary evaluators — chips with truncated names */}
                        <div className="px-4 py-2 border-b border-gray-100">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider shrink-0">Secondary:</span>
                            {thisSecondaries.map(sec => {
                              const secCached = evaluatorCache.get(sec.id);
                              const secEmployee = step5AllEmployees.find(e => e.employeeID === sec.id);
                              const secPhoto = secCached?.photoURL ?? secEmployee?.photoURL ?? '';
                              return (
                                <div key={sec.id} className="flex items-center gap-1.5 pl-1 pr-1.5 py-0.5 border border-indigo-200 rounded-full bg-indigo-50 text-xs shrink-0 group relative">
                                  <EmployeePhoto photoURL={secPhoto} name={sec.name} size="small" />
                                  <span className="font-medium text-indigo-800">{truncName(sec.name)}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPerPrimarySecondaries(prev => {
                                        const next = new Map(prev);
                                        const s = new Set(next.get(primaryId) || []);
                                        s.delete(sec.id);
                                        next.set(primaryId, s);
                                        return next;
                                      });
                                    }}
                                    className="p-0.5 rounded-full text-indigo-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                  {/* Full name tooltip on hover if truncated */}
                                  {sec.name.length > 15 && (
                                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
                                      {sec.name}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {/* Add secondary button + dropdown */}
                            <div className="relative shrink-0">
                              <button
                                type="button"
                                onClick={() => {
                                  setStep5OpenDropdown(isAddDropdownOpen ? null : primaryId);
                                  setStep5SearchTerm('');
                                }}
                                className="flex items-center gap-1 px-2 py-1 border-2 border-dashed border-indigo-300 rounded-full text-indigo-500 hover:border-indigo-400 hover:bg-indigo-50 transition-all text-[11px] font-medium cursor-pointer"
                              >
                                <Plus className="w-3 h-3" />
                                Add
                              </button>

                              {isAddDropdownOpen && (
                                <div
                                  className={`absolute left-0 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50 ${dropdownAbove ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                                  ref={el => { if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }}
                                >
                                  <div className="p-2 border-b border-gray-100">
                                    <div className="relative">
                                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                      <input
                                        type="text"
                                        placeholder="Search evaluator..."
                                        value={step5SearchTerm}
                                        onChange={e => setStep5SearchTerm(e.target.value)}
                                        autoFocus
                                        className="w-full pl-7 pr-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                      />
                                    </div>
                                  </div>
                                  <div className="max-h-48 overflow-y-auto">
                                    {(() => {
                                      const shown = searchLower
                                        ? filteredCandidates.filter(e =>
                                          (e.name ?? '').toLowerCase().includes(searchLower) ||
                                          (e.title ?? '').toLowerCase().includes(searchLower) ||
                                          (e.department ?? '').toLowerCase().includes(searchLower)
                                        )
                                        : filteredCandidates.slice(0, 50);
                                      return shown.length === 0 ? (
                                        <p className="text-center py-4 text-xs text-gray-500">
                                          {searchLower ? 'No candidates found' : 'No employees available'}
                                        </p>
                                      ) : (
                                        <>
                                          {shown.slice(0, 50).map(candidate => (
                                            <button
                                              key={candidate.employeeID}
                                              type="button"
                                              onClick={() => {
                                                setPerPrimarySecondaries(prev => {
                                                  const next = new Map(prev);
                                                  const s = new Set(next.get(primaryId) || []);
                                                  s.add(candidate.employeeID);
                                                  next.set(primaryId, s);
                                                  return next;
                                                });
                                                setStep5OpenDropdown(null);
                                                setStep5SearchTerm('');
                                              }}
                                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-indigo-50 transition-colors text-left cursor-pointer"
                                            >
                                              <EmployeePhoto photoURL={candidate.photoURL} name={candidate.name} size="small" />
                                              <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">{candidate.name}</p>
                                                <p className="text-xs text-gray-500 truncate">{candidate.title ?? ''}{candidate.department ? ` · ${candidate.department}` : ''}</p>
                                              </div>
                                            </button>
                                          ))}
                                          {!searchLower && filteredCandidates.length > 50 && (
                                            <p className="text-center py-2 text-[10px] text-gray-400">Type to search {filteredCandidates.length - 50} more...</p>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Row 3: Employees — collapsed with overflow detection */}
                        <div className="px-4 py-2">
                          <div className={`flex flex-wrap gap-1.5 ${!isExpanded && primaryEmployees.length > 10 ? 'max-h-[80px] overflow-hidden' : ''}`}>
                            {primaryEmployees.map(emp => (
                              <div key={emp.employeeID} className="flex items-center gap-1 pl-0.5 pr-2 py-0.5 bg-gray-50 border border-gray-200 rounded-full text-xs shrink-0 group relative">
                                <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="tiny" />
                                <span className="font-medium text-gray-700">{truncName(emp.name)}</span>
                                {/* Full name tooltip on hover if truncated */}
                                {emp.name.length > 15 && (
                                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
                                    {emp.name}
                                  </div>
                                )}
                              </div>
                            ))}
                            {primaryEmployees.length === 0 && (
                              <p className="text-xs text-gray-400 italic">No employees in this group</p>
                            )}
                          </div>
                          {(isExpanded || primaryEmployees.length > 10) && (
                            <button
                              type="button"
                              onClick={() => setStep5ExpandedCards(prev => {
                                const next = new Set(prev);
                                if (next.has(primaryId)) next.delete(primaryId);
                                else next.add(primaryId);
                                return next;
                              })}
                              className="flex items-center gap-1 mt-1 text-[11px] text-indigo-500 hover:text-indigo-700 font-medium cursor-pointer transition-colors"
                            >
                              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              {isExpanded ? 'Show less' : `Show all ${primaryEmployees.length} employees`}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Click outside to close any open dropdown */}
              {step5OpenDropdown !== null && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => {
                    setStep5OpenDropdown(null);
                    setStep5SearchTerm('');
                  }}
                />
              )}

              {/* Action buttons */}
              <div className="flex gap-3 pt-4 border-t-2 border-gray-200 shrink-0">
                <button
                  onClick={handleCreate}
                  disabled={loading || totalSecondaryCount === 0}
                  className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition-all shadow-lg ${loading || totalSecondaryCount === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 hover:shadow-xl cursor-pointer'
                    }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Creating...
                    </span>
                  ) : (
                    `Create Round (${totalRoundCount} round${totalRoundCount !== 1 ? 's' : ''})`
                  )}
                </button>
                <button
                  onClick={handleBack}
                  className="px-6 py-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-bold text-lg shadow-md hover:shadow-lg cursor-pointer"
                >
                  Back
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default NewRoundModal;
